import { Context, createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

export interface PendingChangesContextType<T = any> {
	pendingChanges: boolean;
	isItemChanged: (item: T) => boolean;
	setItemChanged: (item: T, changed: boolean) => void;
	clearAllChanges: () => void;
}

const defaultContextValues = {
	pendingChanges: false,
	isItemChanged: undefined,
	setItemChanged: undefined,
	clearAllChanges: undefined,
};

function createPendingChangesContext<T = any>(contextValues?: PendingChangesContextType<T>) {
	return createContext<PendingChangesContextType<T>>(contextValues ?? defaultContextValues);
}
export const PendingChangesContext = createPendingChangesContext();
export function getPendingChangesContext<T = any>() {
	return PendingChangesContext as Context<PendingChangesContextType<T>>;
}
export const usePendingChangesContext = () => useContext(PendingChangesContext);
export function useUpdatePendingChanges<T = any>(type: T) {
	const { setItemChanged } = usePendingChangesContext();
	const update = useCallback(
		(changed: boolean) => setItemChanged && setItemChanged(type, changed),
		[setItemChanged, type],
	);
	return update;
}

interface PendingChangesProviderProps {
	children: ReactNode;
}

export function PendingChangesProvider<T = any>(props: PendingChangesProviderProps) {
	const { children } = props;

	const [itemsChanged, setItemsChanged] = useState(() => new Set<T>());

	const isItemChanged = useCallback((item: T) => itemsChanged.has(item), [itemsChanged]);
	const setItemChanged = useCallback(
		(item: T, changed: boolean) => {
			const items = new Set(itemsChanged);
			if (changed) items.add(item);
			else items.delete(item);
			setItemsChanged(items);
		},
		[itemsChanged, setItemsChanged],
	);

	const clearAllChanges = useCallback(() => setItemsChanged(new Set<T>()), [setItemsChanged]);

	const pendingChanges = useMemo(() => itemsChanged.size > 0, [itemsChanged.size]);

	return (
		<PendingChangesContext.Provider value={{ pendingChanges, isItemChanged, setItemChanged, clearAllChanges }}>
			{children}
		</PendingChangesContext.Provider>
	);
}
